import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { API } from "@/lib/api";
import { useSiteChrome } from "@/lib/site";
import { BlogHeader, BlogFooter } from "@/components/blog/BlogChrome";

const group = (posts) => posts.reduce((m, p) => { const k = p.series || "Articles"; (m[k] = m[k] || []).push(p); return m; }, {});

const Chapter = ({ p, i }) => (
  <motion.li initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ delay: Math.min(i, 6) * 0.04 }}>
    <Link to={`/blog/${p.slug}`} data-testid={`blog-chapter-${p.slug}`} className="group grid grid-cols-[52px_1fr] gap-4 border-b border-[#e2e0d6] py-6 md:grid-cols-[72px_1fr_110px]">
      <span className="font-['Georgia'] text-[34px] leading-none text-[#0f7c78] md:text-[42px]">{String(p.chapter || i + 1).padStart(2, "0")}</span>
      <span>
        <span className="block font-['Georgia'] text-[21px] leading-snug text-[#17201b] transition-colors group-hover:text-[#0f7c78] md:text-[24px]">{p.title}</span>
        {p.summary && <span className="mt-2 block text-[15px] leading-relaxed text-[#4c5a53]">{p.summary}</span>}
        <span className="mt-2 block text-[13px] font-semibold text-[#0f7c78] md:hidden">{p.reading_minutes} min read →</span>
      </span>
      <span className="hidden text-right text-[13px] font-semibold text-[#4c5a53] md:block">{p.reading_minutes} min read<span className="mt-1 block text-[#0f7c78] opacity-0 transition-opacity group-hover:opacity-100">Read →</span></span>
    </Link>
  </motion.li>
);

export default function BlogIndex() {
  useSiteChrome("Blog | Art of Resume");
  const [posts, setPosts] = useState(null);
  useEffect(() => { axios.get(`${API}/blog/posts`).then((r) => setPosts(Array.isArray(r.data) ? r.data : [])).catch(() => setPosts([])); }, []);
  const series = group(posts || []);
  return (
    <div className="min-h-screen bg-[#f6f4ee]" data-testid="blog-index">
      <BlogHeader />
      <main className="mx-auto max-w-[960px] px-5">
        <section className="pb-10 pt-16 md:pt-24">
          <p className="text-[12px] font-bold tracking-[0.18em] text-[#0f7c78]">THE ART OF RESUME BLOG</p>
          <h1 className="mt-4 font-['Georgia'] text-4xl font-normal leading-[1.05] text-[#17201b] sm:text-5xl lg:text-6xl">Long reads that <em className="text-[#e8703a]">connect every dot.</em></h1>
          <p className="mt-5 max-w-[640px] text-[17px] leading-relaxed text-[#4c5a53]">Textbook depth, told as a story. Each series walks you from the first question to working understanding, one chapter leading into the next.</p>
        </section>
        {posts === null && <p className="py-10 text-[#4c5a53]" data-testid="blog-loading">Opening the contents…</p>}
        {posts && !posts.length && <p className="py-10 text-[#4c5a53]" data-testid="blog-empty">The first series is being written. Please check back soon.</p>}
        {Object.entries(series).map(([name, list]) => (
          <section key={name} className="mt-6" data-testid="blog-series">
            <div className="flex flex-wrap items-end justify-between gap-3 border-b-2 border-[#17201b] pb-4">
              <div><p className="text-[12px] font-bold tracking-[0.18em] text-[#4c5a53]">SERIES · CONTENTS</p><h2 className="mt-1 font-['Georgia'] text-[28px] text-[#17201b] md:text-[34px]">{name}</h2></div>
              <p className="text-[14px] font-semibold text-[#4c5a53]">{list.length} chapters · {list.reduce((a, p) => a + (p.reading_minutes || 0), 0)} min</p>
            </div>
            <ol>{list.map((p, i) => <Chapter key={p.slug} p={p} i={i} />)}</ol>
            {list[0] && <Link to={`/blog/${list[0].slug}`} data-testid="blog-start-series" className="mt-8 inline-block rounded-full bg-[#0f7c78] px-6 py-3 text-[15px] font-semibold text-white transition-transform hover:-translate-y-0.5">Start with chapter 1 →</Link>}
          </section>
        ))}
      </main>
      <BlogFooter />
    </div>
  );
}
